/**
 * THE PLATFORM'S OWN HOSTS — every absolute address the Pro Hub hands out, from ONE place
 * (TENEAN B.4; the same names as image-os-web src/lib/hosts.ts and studios-backend
 * _shared/hosts.ts, so the three surfaces flip together).
 *
 * Peter, 2026-09-21: "Everything becomes Tenean." imageos.co retires and the product moves to
 * app.tenean.com / pro.tenean.com. Before this file the Owner Hub's host was a literal in
 * eleven pro-facing files: every door this hub calls (designer, library, ig-scan, ig-refresh,
 * reviews, checks, signature, photo), the /q/ scan base on the pro's own code card, the /m/
 * media root three pages re-rooted a photo on, the support address, and the two legal
 * documents that name where the Service is served. A domain retirement bites on the one
 * literal nobody found, so the cutover is ONE EDIT here and hosts.test.ts refuses any literal
 * that regrows in src/.
 *
 * WHAT STAYS LITERAL, deliberately: src/lib/qr.ts's redirector fence — a fence lists every host
 * that ever was ours, old and new alike, and is widened first and narrowed last (B.7).
 */

import { CELL_MEDIA_ORIGIN, isCell } from './cell/config';

/** The Owner Hub — the app whose doors this hub calls, and the host every /m/ and /q/ address lives on. */
export const OWNER_HUB_ORIGIN = 'https://app.tenean.com'; // THE CUTOVER 2026-09-21 (0a91ca90)

/** This hub. Named so the legal pages and the sign-in footer can say where the Service is served. */
export const PRO_HUB_ORIGIN = 'https://pro.tenean.com';

/** The bare host, for copy that names it ("imageos.co") and for the platform's mail domain. */
export const bareHost = (origin: string): string => origin.replace(/^https?:\/\//, '').replace(/\/+$/, '');

export const PLATFORM_DOMAIN = bareHost(OWNER_HUB_ORIGIN);

/**
 * THE MAIL DOMAIN IS NOT THE WEB HOST (the cutover, 2026-09-21). Platform mail — support@, hello@,
 * hub@ — is received on imageos.co: that is the domain Resend has verified and the one Cloudflare
 * Email Routing forwards. The web moving to app.tenean.com did not move MX, so support@app.tenean.com
 * would bounce. Same constant, same name, same reason as studios-backend _shared/hosts.ts; moving
 * mail to tenean.com is its own workstream and flips THIS constant when it lands.
 */
export const PLATFORM_MAIL_DOMAIN = 'imageos.co';

/** `support@`, `hello@` … on the platform's MAIL domain, never the web host. */
export const platformAddress = (local: string): string => `${local}@${PLATFORM_MAIL_DOMAIN}`;

/**
 * THE INBOX DOMAIN — where a pro WRITES to us (Peter, 2026-09-22). tenean.com receives through
 * Cloudflare Email Routing; sending still leaves from PLATFORM_MAIL_DOMAIN until Resend verifies
 * tenean.com. Same pair as image-os-web src/lib/hosts.ts.
 */
export const PLATFORM_INBOX_DOMAIN = 'tenean.com';
export const inboxAddress = (local: string): string => `${local}@${PLATFORM_INBOX_DOMAIN}`;

/** The Owner Hub door this hub posts to — VITE_MEDIA_DOOR wins when a preview sets one. */
export const doorOrigin = (): string =>
  ((import.meta.env.VITE_MEDIA_DOOR as string | undefined) ?? OWNER_HUB_ORIGIN).replace(/\/+$/, '');

/** `${origin}/<path>` — a root-relative path made absolute against the Owner Hub (or a given origin). */
export function absolute(path: string, origin: string = OWNER_HUB_ORIGIN): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${origin.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/** `/m/<key>` on the media host. */
export const mediaUrl = (key: string, origin: string = OWNER_HUB_ORIGIN): string =>
  absolute(`/m/${key.replace(/^\/+/, '')}`, origin);

/** A stored `/m/…` path made absolute on the media host; an absolute URL passes through untouched. */
/** Where /m/ media is read: the Owner Hub host, or on the cell build (M28) the media copy's staging host. */
export const mediaOrigin = (): string => (isCell() && CELL_MEDIA_ORIGIN ? CELL_MEDIA_ORIGIN : OWNER_HUB_ORIGIN);

export const mediaAbs = (url: string, origin: string = mediaOrigin()): string =>
  url.startsWith('/m/') ? absolute(url, origin) : url;

/** The address a printed code opens. */
export const qrUrl = (slug: string): string => `${OWNER_HUB_ORIGIN}/q/${slug}`;
